// C.1 — батч для оставшихся Модов Синтетика (v6 ID).
//
// Покрывает то, что не реализовано в modifications.js MOD_REGISTRY:
//   - Стат/keyword-моды без триггера (Защитная оболочка, Заводской имплант)
//   - Триггерные моды (boevoy_stim, krovavy_implant, krovyanoy_nasos,
//     implant_berserka, implant_mertvetsa, iskin, stek_usilitel)
//   - passive_modifier юнитов (Аугментированный гигант, Биомех,
//     Имплант-аддикт, Сквилл-лич — динамический buff_self_per_mod_count)
//   - ally_received_mod хук (Уличный наёмник, Лаборатория мутаций)
//
// Триггерные моды обходят все юниты обеих сторон в нужном событии и проверяют
// `card.modifications.some(m => m.id === MOD_ID)`. Если есть — срабатывает.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { addModEffect, setModHooks } from '../modifications.js';
import { generateRandomMod } from './mod-factory.js';
import { applyPermanentBuff } from '../../../buffs.js';
import { applyDamage } from '../../../combat.js';
import { healUnit } from '../../livlife/heal.js';
import {
  registerOnEndOfTurnExtraHandler,
  registerOnTurnStartExtraHandler,
  registerOnUnitDiedExtraHandler,
  registerUnitLeftFieldExtraHandler
} from '../../deus/hooks.js';
import { registerOnAttackTrigger } from '../../livlife/on-attack-triggers.js';

// ============================================================
// === Расширение MOD_REGISTRY (стат/keyword моды без триггера) ===
// ============================================================
// addModEffect добавляет в реестр attachMod-эффектов. Используется чтобы
// для новых модов не было warn'а в attachMod.

// Защитная оболочка — armor-mod, +0/2/0 (стат-бонус).
addModEffect('syn_mod_zashchitnaya_obolochka', () => ({
  atk: 0, armor: 2, maxHp: 0, hpDelta: 0,
  keywords: [],
  description: '+0/+2/0 (защита)'
}));

// Заводской имплант — +1 mod_limit. Если 2+ ДРУГИХ Модов — также +2/0/0.
// Реализация: при attach: overloadThreshold +=1. Бонус +2/0/0 пересчитывается
// в recomputeModCountBonus.
addModEffect('syn_mod_zavodskoy', (state, target) => {
  // +1 mod_limit (Перегрузка отодвигается).
  target.overloadThreshold = (target.overloadThreshold || 3) + 1;
  return {
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [],
    description: '+1 mod limit (+2/0/0 если 2+ других Модов)'
  };
});

// === Стабы для триггерных модов — attach не меняет статы ===
// Эффекты обрабатываются хуками ниже.
for (const id of [
  'syn_mod_boevoy_stim',
  'syn_mod_implant_berserka',
  'syn_mod_implant_mertvetsa',
  'syn_mod_iskin',
  'syn_mod_krovavy_implant',
  'syn_mod_krovyanoy_nasos',
  'syn_mod_stek_usilitel'
]) {
  addModEffect(id, () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [],
    description: 'триггерный Мод'
  }));
}

// ============================================================
// === ТРИГГЕРНЫЕ МОДЫ                                         ===
// ============================================================

function unitsHavingMod(state, modId) {
  const result = [];
  if (!state?.players) return result;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u || !u.isAlive || !u.isAlive()) continue;
      if (Array.isArray(u.modifications) && u.modifications.some(m => m.id === modId)) {
        result.push({ unit: u, ownerIdx: pIdx });
      }
    }
  }
  return result;
}

// --- syn_mod_boevoy_stim ---
// turn_start (even turn): если Герой Бишор — Герою +1/0/0 perm.
const BOEVOY_STIM_ID = 'syn_mod_boevoy_stim';
registerOnTurnStartExtraHandler((state) => {
  const turn = state.currentTurn ?? 0;
  if (turn % 2 !== 0) return;
  for (const { unit, ownerIdx } of unitsHavingMod(state, BOEVOY_STIM_ID)) {
    if (unit.current?.silenced) continue;
    const hero = state.players[ownerIdx]?.hero;
    if (!hero || hero.id !== 'syn_bishor') continue;
    hero.base.atk += 1; hero.current.atk += 1;
    state.log?.add(`⚙ Боевой стим [${state.players[ownerIdx]?.name}]: Бишор +1 atk`);
  }
});

// --- syn_mod_implant_berserka ---
// turn_end: носитель получает 2 урона.
const BERSERKA_ID = 'syn_mod_implant_berserka';
registerOnEndOfTurnExtraHandler((state) => {
  // Снимок целей до применения (юнит может умереть и активировать deathrattle).
  const snapshot = unitsHavingMod(state, BERSERKA_ID).slice();
  for (const { unit } of snapshot) {
    if (!unit.isAlive || !unit.isAlive()) continue;
    if (unit.current?.silenced) continue;
    applyDamage(unit, 2, { state, source: { id: BERSERKA_ID, name_ru: 'Имплант берсерка' } });
  }
});

// --- syn_mod_implant_mertvetsa ---
// deathrattle: если Герой Уэйн — +1/0/0 Герою.
const MERTVETSA_ID = 'syn_mod_implant_mertvetsa';
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead) return;
  if (dead.current?.silenced) return;
  if (!Array.isArray(dead.modifications)) return;
  if (!dead.modifications.some(m => m.id === MERTVETSA_ID)) return;
  const hero = state.players?.[payload.ownerIdx]?.hero;
  if (!hero || hero.id !== 'syn_uein') return;
  hero.base.atk += 1; hero.current.atk += 1;
  state.log?.add(`💀 Имплант мертвеца [${state.players[payload.ownerIdx]?.name}]: Уэйн +1 atk`);
});

// --- syn_mod_iskin ---
// turn_end: +1 случайный Мод из колоды в руку.
const ISKIN_ID = 'syn_mod_iskin';
registerOnEndOfTurnExtraHandler((state) => {
  for (const { unit, ownerIdx } of unitsHavingMod(state, ISKIN_ID)) {
    if (unit.current?.silenced) continue;
    const p = state.players[ownerIdx];
    if (!p?.deck || !p.hand) continue;
    if (p.hand.length >= 10) continue;
    const modsInDeck = p.deck.filter(c => c?.type === 'mod');
    if (modsInDeck.length === 0) continue;
    const idx = state.rng?.int ? state.rng.int(modsInDeck.length) : Math.floor(Math.random() * modsInDeck.length);
    const pick = modsInDeck[idx];
    const dIdx = p.deck.indexOf(pick);
    if (dIdx < 0) continue;
    p.deck.splice(dIdx, 1);
    p.hand.push(pick);
    state.log?.add(`🧠 Подключить ИИскина [${p.name}]: ${pick.name_ru} → в руку`);
  }
});

// --- syn_mod_krovavy_implant ---
// on_attack (своя атака + контратака): если Бишор — Герой +1/0/0 perm.
// Реализация: после fireOnAttackTrigger, обходим всех юнитов с этим модом.
// Но fireOnAttackTrigger срабатывает по cardId attacker'а. Удобнее повесить
// общий hook через registerOnAttackTrigger на «любой attacker».
// Альтернатива — глобальный обходник всех attacker'ов: при любой атаке
// проверяем mods. Это требует низкоуровневой подписки на applyDamage events.
//
// Тут используем приём: универсальный handler для любого id. К сожалению
// reg-by-id Map не подходит — нужно общее уведомление. Сейчас единственный
// способ — вешать триггер per-cardId. Сделаем триггер на ВСЕ Синтетикс-юниты
// которые могут носить Мод. Простоты ради — повесим на все известные юниты
// и Героев Синтетика.
//
// Лучшее решение: расширить on-attack-triggers.js «global»-хук. Но для C.1
// текущее ограничение приемлемо: повесим триггеры на популярные носители.
const KROVAVY_ID = 'syn_mod_krovavy_implant';
const KROVAVY_CANDIDATE_IDS = new Set();
function ensureKrovavyTrigger(unitId) {
  if (KROVAVY_CANDIDATE_IDS.has(unitId)) return;
  KROVAVY_CANDIDATE_IDS.add(unitId);
  registerOnAttackTrigger(unitId, (state, attacker) => {
    if (!state?.players || !attacker) return;
    if (!Array.isArray(attacker.modifications)) return;
    if (!attacker.modifications.some(m => m.id === KROVAVY_ID)) return;
    if (attacker.current?.silenced) return;
    // БАГ-фикс (Этап 56.43): у Card свойство ownerId, не ownerIdx —
    // attacker.ownerIdx был undefined → hero undefined → триггер НИКОГДА
    // не срабатывал (Кровавый имплант не давал Бишору +1 atk).
    const hero = state.players[attacker.ownerId]?.hero;
    if (!hero || hero.id !== 'syn_bishor') return;
    hero.base.atk += 1; hero.current.atk += 1;
    state.log?.add(`🩸 Кровавый имплант [${state.players[attacker.ownerId]?.name}]: Бишор +1 atk`);
  });
}
// Регистрируем на все известные юниты Синтетика. Это перекроет 95% сценариев.
// Не-Синтетик юниты не несут моды Синтетика, поэтому не нужны.
const SYNTETICS_UNIT_IDS_FOR_TRIGGERS = [
  'syn_augmented_giant', 'syn_bioenginer', 'syn_biomeh', 'syn_bishor_lich',
  'syn_chorny_rynok', 'syn_diler', 'syn_doctor_shram', 'syn_fixer',
  'syn_gasper', 'syn_glavar_bandy', 'syn_hirurg_bez_litsenzii',
  'syn_hirurg_mehanik', 'syn_implant_addikt', 'syn_implantirovanny',
  'syn_implantolog', 'syn_implantoman', 'syn_kiberpsih',
  'syn_konveyer_tel', 'syn_lab_mutatsiy', 'syn_laborant_nii',
  'syn_loborganik', 'syn_naletchik', 'syn_narcolab', 'syn_perekup',
  'syn_podpolny_boets', 'syn_podryvnik', 'syn_rast_lich',
  'syn_shkurodyor', 'syn_skvill_lich', 'syn_smertnik',
  'syn_spisany_dron', 'syn_stimulyatorshchik', 'syn_temny_netrunner',
  'syn_torchok', 'syn_tseh_implantov', 'syn_uein_lich',
  'syn_ulichny_naemnik', 'syn_vyshibala', 'syn_zazyvala'
];
for (const id of SYNTETICS_UNIT_IDS_FOR_TRIGGERS) ensureKrovavyTrigger(id);

// --- syn_mod_krovyanoy_nasos ---
// turn_start: носитель +2 hp (cap по max).
const NASOS_ID = 'syn_mod_krovyanoy_nasos';
registerOnTurnStartExtraHandler((state) => {
  for (const { unit } of unitsHavingMod(state, NASOS_ID)) {
    if (unit.current?.silenced) continue;
    healUnit(state, unit, 2, { id: NASOS_ID, name_ru: 'Кровяной насос' });
  }
});

// --- syn_mod_stek_usilitel ---
// turn_end: все союзники носителя +1 hp.
const STEK_ID = 'syn_mod_stek_usilitel';
registerOnEndOfTurnExtraHandler((state) => {
  for (const { unit, ownerIdx } of unitsHavingMod(state, STEK_ID)) {
    if (unit.current?.silenced) continue;
    const p = state.players[ownerIdx];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u || !u.isAlive || !u.isAlive()) continue;
      healUnit(state, u, 1, { id: STEK_ID, name_ru: 'Стек-усилитель' });
    }
  }
});

// ============================================================
// === passive_modifier юнитов                                  ===
// ============================================================

// API: recomputeModCountBonus(card) — пересчёт динамического buff_self_per_mod_count
// и +2/0/0 от Заводского импланта. Вызывается из attachMod / detachMod.

export function recomputeModCountBonus(state, card) {
  if (!card || card.type !== 'unit') return;
  const modCount = Array.isArray(card.modifications) ? card.modifications.length : 0;
  // Сначала вычитаем старый бонус.
  const oldBonus = card.modCountBonus || { atk: 0, armor: 0, hp: 0 };
  if (oldBonus.atk) { card.base.atk -= oldBonus.atk; card.current.atk = Math.max(0, card.current.atk - oldBonus.atk); }
  if (oldBonus.armor) {
    card.persistentArmor = Math.max(0, (card.persistentArmor || 0) - oldBonus.armor);
    card.persistentMaxArmor = Math.max(0, (card.persistentMaxArmor || 0) - oldBonus.armor);
    card.current.armor = Math.max(0, (card.current.armor || 0) - oldBonus.armor);
  }
  if (oldBonus.hp) {
    card.base.hp -= oldBonus.hp;
    if (card.current.hp > card.base.hp) card.current.hp = card.base.hp;
  }

  // Считаем новый бонус.
  let perMod = null;
  let extraStatic = null;
  if (card.current?.silenced) {
    // Под Молчанием пассивки выключены — нулевой бонус.
    card.modCountBonus = { atk: 0, armor: 0, hp: 0 };
    return;
  }
  switch (card.id) {
    case 'syn_biomeh':         perMod = { atk: 0, armor: 1, hp: 0 }; break;
    case 'syn_implant_addikt': perMod = { atk: 0, armor: 0, hp: 2 }; break;
    case 'syn_skvill_lich':    perMod = { atk: 1, armor: 0, hp: 0 }; break;
    default: break;
  }
  // Заводской имплант: если на носителе 2+ ДРУГИХ Модов — +2/0/0.
  // (Сам Заводской — один из модов; считаем «другие» как modCount - 1.)
  const hasZavodskoy = Array.isArray(card.modifications)
    && card.modifications.some(m => m.id === 'syn_mod_zavodskoy');
  if (hasZavodskoy && modCount - 1 >= 2) {
    extraStatic = { atk: 2, armor: 0, hp: 0 };
  }
  const newBonus = { atk: 0, armor: 0, hp: 0 };
  if (perMod) {
    newBonus.atk += perMod.atk * modCount;
    newBonus.armor += perMod.armor * modCount;
    newBonus.hp += perMod.hp * modCount;
  }
  if (extraStatic) {
    newBonus.atk += extraStatic.atk;
    newBonus.armor += extraStatic.armor;
    newBonus.hp += extraStatic.hp;
  }
  // Применяем новый бонус.
  if (newBonus.atk) { card.base.atk += newBonus.atk; card.current.atk += newBonus.atk; }
  if (newBonus.armor) {
    card.persistentArmor = (card.persistentArmor || 0) + newBonus.armor;
    card.persistentMaxArmor = (card.persistentMaxArmor || 0) + newBonus.armor;
    card.current.armor = (card.current.armor || 0) + newBonus.armor;
  }
  if (newBonus.hp) {
    card.base.hp += newBonus.hp;
    card.current.hp += newBonus.hp;
  }
  card.modCountBonus = newBonus;
}

// === syn_glavar_bandy — aura: пока жив, СВОИ юниты с ≥1 Модом получают +1/0/0 ===
// QA-фикс (Этап 81.3): аура была только registerImplementedAbility('aura') БЕЗ
// логики (стаб) — юниты с Модом не получали +1/0/0. Реализована по образцу
// recomputeDirectorAura: динамический пересчёт с трекингом применённой дельты
// в card.glavarBandyApplied (только +atk). Пересчёт вызывается при навеске/снятии
// Мода (через setModHooks → recomputeModCountBonus), появлении/уходе/смерти юнита.
const GLAVAR_BANDY_ID = 'syn_glavar_bandy';
export function recomputeGlavarBandyAura(state) {
  if (!state?.players) return;
  for (let idx = 0; idx < 2; idx++) {
    const p = state.players[idx];
    if (!p?.field) continue;
    const glavarAlive = p.field.flat().some(
      u => u && u.id === GLAVAR_BANDY_ID && u.isAlive && u.isAlive() && !u.current?.silenced
    );
    for (const u of p.field.flat()) {
      if (!u) continue;
      const hasMod = Array.isArray(u.modifications) && u.modifications.length > 0;
      const want = (glavarAlive && hasMod) ? 1 : 0;   // только +1 атаки
      const applied = u.glavarBandyApplied || 0;
      const d = want - applied;
      if (d === 0) continue;
      u.current.atk = Math.max(0, u.current.atk + d);  // как директор-аура: только current
      u.glavarBandyApplied = want;
    }
  }
}
registerUnitSummonedExtraHandler((state) => recomputeGlavarBandyAura(state));
registerUnitLeftFieldExtraHandler((state) => recomputeGlavarBandyAura(state));
registerOnUnitDiedExtraHandler((state) => recomputeGlavarBandyAura(state));

// Аугментированный гигант — passive_modifier increase_mod_limit:1.
// При onSummon: overloadThreshold +1 (анал. Списанному дрону).
import { registerUnitSummonedExtraHandler } from '../../deus/hooks.js';
registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  if (!card) return;
  if (card.id === 'syn_augmented_giant') {
    card.overloadThreshold = (card.overloadThreshold || 3) + 1;
  }
  if (card.id === 'syn_skvill_lich') {
    // Этап 56-fix: по тексту карты «Сбой при 7+ Модах» → overloadThreshold = 7
    // (было ошибочно 4).
    card.overloadThreshold = 7;
  }
  // После выставления юнита — пересчёт mod-зависимых бонусов
  // (на самом деле modCount=0, бонус 0; но для будущих случаев).
  recomputeModCountBonus(state, card);
  // Этап 56-fix: Раст-лич — аура «пока Раст-юнит жив, новый союзник получает
  // 2 случайных Мода (генерация)». Раньше была только stub-регистрация без
  // логики. Источник — любой ДРУГОЙ живой Раст-лич на стороне владельца
  // (сам себя не баффает при выставлении). С авто-Модом Героя Раста итого = 3.
  if (card.type === 'unit' && card.isAlive && card.isAlive()) {
    const ownerP = state.players?.[ownerIdx];
    const hasRastLich = ownerP?.field?.flat?.().some(
      u => u && u !== card && u.id === 'syn_rast_lich' && u.isAlive && u.isAlive() && !u.current?.silenced
    );
    if (hasRastLich) {
      generateRandomMod(state, ownerIdx, { destination: 'attach', target: card });
      if (card.isAlive && card.isAlive() && !card.overloaded) {
        generateRandomMod(state, ownerIdx, { destination: 'attach', target: card });
      }
      state.log?.add?.(`✨ Раст-лич [${ownerP.name}]: ${card.name_ru} получает 2 Мода (аура)`);
    }
  }
});
registerImplementedAbility('syn_augmented_giant', 'trigger');
registerImplementedAbility('syn_skvill_lich', 'trigger');
registerImplementedAbility('syn_biomeh', 'trigger');
registerImplementedAbility('syn_implant_addikt', 'trigger');

// ============================================================
// === ally_received_mod hook                                  ===
// ============================================================
// Реестр триггеров «союзник получил Мод». Вызывается из attachMod после
// успешного attach.

const _onAllyReceivedModTriggers = new Map();
export function registerOnAllyReceivedMod(cardId, fn) {
  if (typeof cardId !== 'string' || typeof fn !== 'function') return;
  _onAllyReceivedModTriggers.set(cardId, fn);
}

export function fireOnAllyReceivedMod(state, targetCard, modDef) {
  if (!state?.players || !targetCard) return;
  if (_onAllyReceivedModTriggers.size === 0) return;
  const ownerIdx = targetCard.ownerId;
  const owner = state.players[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || !u.isAlive || !u.isAlive()) continue;
    if (u.current?.silenced) continue;
    const fn = _onAllyReceivedModTriggers.get(u.id);
    if (!fn) continue;
    try { fn(state, u, targetCard, modDef); }
    catch (e) { console.warn(`[Корпы] ally_received_mod trigger ${u.id} упал:`, e); }
  }
}

// --- syn_ulichny_naemnik — +1 atk self perm ---
registerOnAllyReceivedMod('syn_ulichny_naemnik', (state, observer) => {
  applyPermanentBuff(state, observer, { atk: 1, armor: 0, hp: 0 },
    observer.id, observer.name_ru);
});
registerImplementedAbility('syn_ulichny_naemnik', 'trigger');

// --- syn_lab_mutatsiy — 50/50: 3 dmg random enemy ИЛИ 1 random mod на enemy ---
registerOnAllyReceivedMod('syn_lab_mutatsiy', (state, observer) => {
  const enemy = state.players?.[1 - observer.ownerId];
  if (!enemy?.field) return;
  const enemies = [];
  for (const row of enemy.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive()) enemies.push(u);
  }
  if (enemies.length === 0) return;
  const tIdx = state.rng?.int ? state.rng.int(enemies.length) : Math.floor(Math.random() * enemies.length);
  const target = enemies[tIdx];
  const roll = state.rng?.int ? state.rng.int(2) : Math.floor(Math.random() * 2);
  if (roll === 0) {
    applyDamage(target, 3, { state, source: observer });
    state.log?.add(`🧪 Лаборатория мутаций [${state.players[observer.ownerId]?.name}]: 3 dmg → ${target.name_ru}`);
  } else {
    // Раздаём случайный Мод на врага через mod-factory.
    generateRandomMod(state, target.ownerId, { destination: 'attach', target });
  }
});
registerImplementedAbility('syn_lab_mutatsiy', 'trigger');

// --- syn_chorny_rynok — ally_died → +1 random mod в руку ---
// Тоже относится к этой категории (мод-генерация). Хук от ally_died.
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  const ownerIdx = payload?.ownerIdx;
  if (!dead || ownerIdx == null) return;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u.id !== 'syn_chorny_rynok') continue;
    if (u.current?.silenced) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    // Этап 24: реальная генерация Мода через mod-factory.
    generateRandomMod(state, ownerIdx, { destination: 'hand' });
  }
});
registerImplementedAbility('syn_chorny_rynok', 'trigger');

// === Этап 56-fix: Имплантированный — EOT-хил по числу Модов ===
// «Пока на этом юните есть ≥1 Мод, в конце каждого хода лечится на количество
// Модов на ВСЕХ твоих юнитах (включая собственные)». Раньше — только stub.
registerOnEndOfTurnExtraHandler((state) => {
  if (!state?.players) return;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    let totalMods = 0;
    const units = [];
    for (const row of p.field) for (const u of row || []) {
      if (!u || !u.isAlive || !u.isAlive()) continue;
      units.push(u);
      totalMods += Array.isArray(u.modifications) ? u.modifications.length : 0;
    }
    for (const u of units) {
      if (u.id !== 'syn_implantirovanny' || u.current?.silenced) continue;
      const selfMods = Array.isArray(u.modifications) ? u.modifications.length : 0;
      if (selfMods >= 1 && totalMods > 0) {
        healUnit(state, u, totalMods, { id: 'syn_implantirovanny', name_ru: 'Имплантированный' });
      }
    }
  }
});

// === Этап 56-fix: Рипер-лич (uein_lich) — триггер «мой юнит умер» ===
// «+1 случайный Мод (генерация) в руку И добери 1 карту». Раньше — только stub.
registerOnUnitDiedExtraHandler((state, payload) => {
  const ownerIdx = payload?.ownerIdx;
  if (ownerIdx == null) return;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u.id !== 'syn_uein_lich') continue;
    if (!u.isAlive || !u.isAlive() || u.current?.silenced) continue;
    generateRandomMod(state, ownerIdx, { destination: 'hand' });
    if (owner.deck?.length > 0 && owner.hand.length < 10) {
      owner.hand.push(owner.deck.shift());
      state.log?.add?.(`🎴 Рипер-лич [${owner.name}]: мой юнит погиб → +Мод в руку + добор`);
    }
  }
});

// === Late-binding регистрация хуков в modifications.js ===
// modifications.js не импортирует mods-batch.js напрямую (избегаем цикла).
// Тут регистрируем функции которые modifications.js вызовет в attachMod/detachMod.
setModHooks({
  fireOnAllyReceivedMod,
  // QA-фикс (Этап 81.3): после пересчёта per-mod бонуса носителя — пересчёт
  // ауры Главаря банды (юнит мог получить/потерять Мод → ±1 атаки).
  recomputeModCountBonus: (state, card) => {
    recomputeModCountBonus(state, card);
    recomputeGlavarBandyAura(state);
  }
});
